import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { authApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Church, Lock, ArrowLeft, Mail, Key, UserPlus, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const PasswordInput = ({ field, value, onChange, placeholder, showPassword, onToggle, ...props }) => (
  <div className="relative">
    <Input
      type={showPassword ? 'text' : 'password'}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="pr-10"
      {...props}
    />
    <button
      type="button"
      onClick={onToggle}
      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
      data-testid={`toggle-${field}-visibility`}
    >
      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
    </button>
  </div>
);

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithGoogle, adminLogin, login, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [activeTab, setActiveTab] = useState('login'); // 'login' or 'register'
  
  // For shared password login
  const [sharedPassword, setSharedPassword] = useState('');
  
  // For email + password login/register
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [subscribeNewsletter, setSubscribeNewsletter] = useState(true);
  const [showPassword, setShowPassword] = useState({});

  const togglePasswordVisibility = (field) => {
    setShowPassword(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const from = location.state?.from?.pathname || '/';

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      if (user.role === 'admin') {
        navigate('/admin', { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    }
  }, [user, navigate, from]);

  const handleGoogleLogin = () => {
    loginWithGoogle();
  };

  // Visitor Registration
  const handleRegister = async (e) => {
    e.preventDefault();
    if (!email || !password || !name) {
      toast.error('Please fill in all fields');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const data = await authApi.register(email.trim(), password, name.trim(), subscribeNewsletter);
      login(data.user, data.session_token);
      toast.success('Account created successfully!');
      navigate('/', { replace: true });
    } catch (error) {
      const errMsg = error?.message || 'Registration failed';
      if (!errMsg.includes('postMessage')) {
        toast.error(errMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  // Visitor/Member Email Login
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }

    setLoading(true);
    try {
      await adminLogin(password, email.trim());
      toast.success('Logged in successfully');
      navigate(from, { replace: true });
    } catch (error) {
      const errMsg = error?.message || 'Invalid email or password';
      if (!errMsg.includes('postMessage')) {
        toast.error(errMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  // Shared admin password login
  const handleSharedPasswordLogin = async (e) => {
    e.preventDefault();
    if (!sharedPassword) {
      toast.error('Please enter the admin password');
      return;
    }

    setLoading(true);
    try {
      await adminLogin(sharedPassword.trim());
      toast.success('Logged in successfully');
      navigate('/admin', { replace: true });
    } catch (error) {
      const errMsg = error?.message || 'Invalid password';
      if (!errMsg.includes('postMessage')) {
        toast.error(errMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center text-amber-700 hover:text-amber-800 mb-6">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Link>

        <Card>
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <Church className="h-12 w-12 text-amber-700 mx-auto mb-4" />
              <h1 className="text-2xl font-serif font-bold text-stone-800">Welcome</h1>
              <p className="text-stone-600">Sign in to St. John's UCC</p>
            </div>

            {!showAdminLogin ? (
              <>
                {/* Google Login */}
                <Button
                  onClick={handleGoogleLogin}
                  className="w-full bg-white hover:bg-stone-50 text-stone-800 border border-stone-300"
                  size="lg"
                  data-testid="google-login-btn"
                >
                  <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  Continue with Google
                </Button>

                <Separator className="my-6" />

                {/* Email Login/Register Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="login">
                      <Mail className="h-4 w-4 mr-2" />
                      Sign In
                    </TabsTrigger>
                    <TabsTrigger value="register">
                      <UserPlus className="h-4 w-4 mr-2" />
                      Create Account
                    </TabsTrigger>
                  </TabsList>
                  
                  {/* Email Login */}
                  <TabsContent value="login">
                    <form onSubmit={handleEmailLogin} className="space-y-4 mt-4">
                      <div>
                        <Label htmlFor="login-email">Email</Label>
                        <Input
                          id="login-email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="your.email@example.com"
                          required
                          data-testid="email-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="login-password">Password</Label>
                        <PasswordInput
                          field="login-password"
                          id="login-password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Your password"
                          required
                          showPassword={showPassword['login-password']}
                          onToggle={() => togglePasswordVisibility('login-password')}
                          data-testid="password-input"
                        />
                      </div>
                      <Button
                        type="submit"
                        className="w-full bg-amber-700 hover:bg-amber-800"
                        disabled={loading}
                        data-testid="email-login-btn"
                      >
                        {loading ? 'Signing in...' : 'Sign In'}
                      </Button>
                    </form>
                  </TabsContent>
                  
                  {/* Registration */}
                  <TabsContent value="register">
                    <form onSubmit={handleRegister} className="space-y-4 mt-4">
                      <div>
                        <Label htmlFor="reg-name">Full Name</Label>
                        <Input
                          id="reg-name"
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="John Smith"
                          required
                          data-testid="register-name-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="reg-email">Email</Label>
                        <Input
                          id="reg-email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="your.email@example.com"
                          required
                          data-testid="register-email-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="reg-password">Password</Label>
                        <PasswordInput
                          field="reg-password"
                          id="reg-password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="At least 6 characters"
                          required
                          showPassword={showPassword['reg-password']}
                          onToggle={() => togglePasswordVisibility('reg-password')}
                          data-testid="register-password-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="reg-confirm">Confirm Password</Label>
                        <PasswordInput
                          field="reg-confirm"
                          id="reg-confirm"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Confirm your password"
                          required
                          showPassword={showPassword['reg-confirm']}
                          onToggle={() => togglePasswordVisibility('reg-confirm')}
                          data-testid="register-confirm-input"
                        />
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={subscribeNewsletter}
                          onChange={(e) => setSubscribeNewsletter(e.target.checked)}
                          className="rounded border-stone-300 text-amber-700 focus:ring-amber-700"
                          data-testid="newsletter-checkbox"
                        />
                        <span className="text-sm text-stone-600">Subscribe to our newsletter</span>
                      </label>
                      <Button
                        type="submit"
                        className="w-full bg-amber-700 hover:bg-amber-800"
                        disabled={loading}
                        data-testid="register-btn"
                      >
                        {loading ? 'Creating Account...' : 'Create Account'}
                      </Button>
                      <p className="text-xs text-stone-500 text-center">
                        Create a visitor account to stay connected with St. John's
                      </p>
                    </form>
                  </TabsContent>
                </Tabs>

                <Separator className="my-6" />

                <div className="text-center">
                  <p className="text-stone-600 mb-3 text-sm">Staff or Admin?</p>
                  <Button
                    variant="outline"
                    onClick={() => setShowAdminLogin(true)}
                    className="w-full"
                    data-testid="show-admin-login-btn"
                  >
                    <Lock className="mr-2 h-4 w-4" />
                    Staff / Admin Login
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Tabs defaultValue="shared" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="shared">
                      <Key className="h-4 w-4 mr-2" />
                      Shared Password
                    </TabsTrigger>
                    <TabsTrigger value="personal">
                      <Mail className="h-4 w-4 mr-2" />
                      Personal Account
                    </TabsTrigger>
                  </TabsList>
                  
                  {/* Shared Password Login */}
                  <TabsContent value="shared">
                    <form onSubmit={handleSharedPasswordLogin} className="space-y-4 mt-4">
                      <div>
                        <Label htmlFor="sharedPassword">Shared Admin Password</Label>
                        <PasswordInput
                          field="shared-password"
                          id="sharedPassword"
                          value={sharedPassword}
                          onChange={(e) => setSharedPassword(e.target.value)}
                          placeholder="Enter shared password"
                          required
                          showPassword={showPassword['shared-password']}
                          onToggle={() => togglePasswordVisibility('shared-password')}
                          data-testid="admin-password-input"
                        />
                      </div>
                      <Button
                        type="submit"
                        className="w-full bg-amber-700 hover:bg-amber-800"
                        disabled={loading}
                        data-testid="admin-login-btn"
                      >
                        {loading ? 'Signing in...' : 'Sign In'}
                      </Button>
                      <p className="text-xs text-stone-500 text-center">
                        Use the shared password provided to staff members
                      </p>
                    </form>
                  </TabsContent>
                  
                  {/* Personal Admin Account */}
                  <TabsContent value="personal">
                    <form onSubmit={handleEmailLogin} className="space-y-4 mt-4">
                      <div>
                        <Label htmlFor="admin-email">Email</Label>
                        <Input
                          id="admin-email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="your.email@example.com"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="admin-password">Password</Label>
                        <PasswordInput
                          field="admin-personal-password"
                          id="admin-password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Your personal password"
                          required
                          showPassword={showPassword['admin-personal-password']}
                          onToggle={() => togglePasswordVisibility('admin-personal-password')}
                        />
                      </div>
                      <Button
                        type="submit"
                        className="w-full bg-amber-700 hover:bg-amber-800"
                        disabled={loading}
                      >
                        {loading ? 'Signing in...' : 'Sign In'}
                      </Button>
                      <p className="text-xs text-stone-500 text-center">
                        Use your personal admin account credentials
                      </p>
                    </form>
                  </TabsContent>
                </Tabs>

                <Separator className="my-6" />

                <div className="text-center">
                  <Button
                    variant="ghost"
                    onClick={() => setShowAdminLogin(false)}
                    className="text-stone-600"
                  >
                    Back to Visitor Login
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-stone-500 text-sm mt-6">
          St. John's United Church of Christ
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
